import { useEffect, useState } from 'react';
import axios from 'axios';
import { Layers, ChevronDown, ChevronRight, RefreshCw, CheckCircle2, XCircle, Trash2, Plus, Play } from 'lucide-react';

interface VmwareIntegration {
  id: number;
  tenant_id: number;
  label: string | null;
  base_url: string;
  username: string;
  verify_tls: boolean;
  enabled: boolean;
  api_type: string | null;
  product_name: string | null;
  product_version: string | null;
  product_build: string | null;
  last_sync_at: string | null;
  last_sync_status: 'ok' | 'error' | null;
  last_error: string | null;
  last_event_at: string | null;
  last_summary: {
    hosts: number; vms: number; events: number; alerts: number; findings: number; truncatedEvents: boolean;
    missingProperties: Record<string, string[]>;
    timingsMs?: { connect: number; hosts: number; vms: number; events: number };
    eventsError?: string;
  } | null;
}

type Feedback = { ok: boolean; message: string } | null;

// Una connessione vSphere (vCenter o ESXi standalone). `integration` assente = form di creazione.
function ConnectionRow({ tenantId, integration, onChanged, startOpen }: {
  tenantId: number; integration: VmwareIntegration | null; onChanged: () => void; startOpen?: boolean;
}) {
  const [open, setOpen] = useState(!!startOpen);
  const [label, setLabel] = useState(integration?.label || '');
  const [baseUrl, setBaseUrl] = useState(integration?.base_url || 'https://');
  const [username, setUsername] = useState(integration?.username || '');
  const [password, setPassword] = useState('');
  const [verifyTls, setVerifyTls] = useState(integration ? integration.verify_tls : true);
  const [enabled, setEnabled] = useState(integration?.enabled || false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setFeedback(null);
    setSaving(true);
    try {
      await axios.put('/api/vmware-integration', {
        id: integration?.id, tenantId, label, baseUrl, username, password: password || undefined, verifyTls, enabled,
      });
      setPassword('');
      setFeedback({ ok: true, message: 'Saved.' });
      onChanged();
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Error while saving the connection' });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!integration) return;
    setTesting(true);
    setFeedback(null);
    try {
      const res = await axios.post(`/api/vmware-integration/${integration.id}/test`);
      const d = res.data.data;
      const missing = Object.entries(d.missingProperties || {}).flatMap(([, v]) => v as string[]);
      setFeedback({
        ok: true,
        message: `Connected to ${d.product} (${d.apiType === 'HostAgent' ? 'standalone ESXi' : 'vCenter'}, build ${d.build}) — ${d.hostCount} host(s), ${d.vmCount} VM(s).`
          + (missing.length ? ` ${missing.length} propert${missing.length === 1 ? 'y' : 'ies'} not available on this version (${missing.join(', ')}).` : ''),
      });
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSync() {
    if (!integration) return;
    setSyncing(true);
    setFeedback(null);
    try {
      const res = await axios.post(`/api/vmware-integration/${integration.id}/sync`);
      const s = res.data.data;
      const t = s.timingsMs ? ` Timing: connect ${(s.timingsMs.connect / 1000).toFixed(1)}s, hosts ${(s.timingsMs.hosts / 1000).toFixed(1)}s, VMs ${(s.timingsMs.vms / 1000).toFixed(1)}s, events ${(s.timingsMs.events / 1000).toFixed(1)}s.` : '';
      setFeedback({
        ok: !s.eventsError,
        message: `Synced: ${s.hosts} host(s), ${s.vms} VM(s) into the Asset Inventory; ${s.findings} finding(s) in Vulnerabilities; `
          + `${s.events} event(s) read, ${s.alerts} alert(s) raised${s.truncatedEvents ? ' (event backlog larger than one cycle — the rest comes with the next sync)' : ''}.`
          + (s.eventsError ? ` Events could not be read (${s.eventsError}) — inventory and findings were saved anyway; events will be retried at the next sync.` : '')
          + t,
      });
      onChanged();
    } catch (err: any) {
      setFeedback({ ok: false, message: err.response?.data?.error || 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  }

  async function handleDelete() {
    if (!integration) return;
    if (!window.confirm(`Remove the connection to ${integration.base_url}? Imported assets and findings stay; they will no longer be updated.`)) return;
    await axios.delete(`/api/vmware-integration/${integration.id}`);
    onChanged();
  }

  const title = integration
    ? `${integration.label || integration.base_url}${integration.product_name ? ` — ${integration.product_name}` : ''}`
    : 'New connection';

  return (
    <div className="border border-gray-100 rounded-lg">
      <button type="button" onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 rounded-lg">
        <span className="inline-flex items-center gap-2 font-medium text-gray-800">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {title}
        </span>
        {integration ? (
          <span className="flex items-center gap-2">
            {integration.api_type && (
              <span className="badge bg-sky-50 text-sky-700">{integration.api_type === 'HostAgent' ? 'ESXi standalone' : 'vCenter'}</span>
            )}
            <span className={`badge ${integration.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
              {integration.enabled ? 'Enabled' : 'Configured, disabled'}
            </span>
          </span>
        ) : (
          <span className="text-xs text-gray-400">not saved yet</span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3">
          <form onSubmit={handleSave} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Label (optional)</label>
              <input value={label} onChange={e => setLabel(e.target.value)} className="input-field text-xs" placeholder="e.g. vCenter sede principale" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">vCenter or ESXi address</label>
              <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} required
                className="input-field font-mono text-xs" placeholder="https://vcenter.customer.local" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Username</label>
              <input value={username} onChange={e => setUsername(e.target.value)} required
                className="input-field font-mono text-xs" placeholder="soc-readonly@vsphere.local" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Password {integration && <span className="text-gray-400">(leave blank to keep)</span>}
              </label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                className="input-field font-mono text-xs" required={!integration} placeholder={integration ? '••••••••' : ''} />
            </div>
            <div className="flex flex-col gap-1.5 pb-1">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={verifyTls} onChange={e => setVerifyTls(e.target.checked)} />
                Verify TLS certificate
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
                Enabled (syncs every 15 minutes)
              </label>
            </div>
            <div className="md:col-span-3">
              <p className="text-xs text-gray-400">
                A <span className="font-medium">read-only</span> role on the root object is enough (inventory, configuration,
                events). vCenter and ESXi ship with a self-signed certificate: if the test reports an untrusted
                certificate, untick "Verify TLS certificate". Works with vSphere 5.x to 8.x and with ESXi hosts without vCenter.
              </p>
            </div>
            <div className="md:col-span-3 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {integration && (
                  <>
                    <button type="button" onClick={handleTest} disabled={testing || syncing} className="btn-secondary flex items-center gap-1 disabled:opacity-50">
                      <RefreshCw size={13} className={testing ? 'animate-spin' : ''} /> Test connection
                    </button>
                    <button type="button" onClick={handleSync} disabled={testing || syncing} className="btn-secondary flex items-center gap-1 disabled:opacity-50">
                      <Play size={13} className={syncing ? 'animate-pulse' : ''} /> {syncing ? 'Syncing…' : 'Sync now'}
                    </button>
                    <button type="button" onClick={handleDelete} className="btn-secondary text-red-600 flex items-center gap-1">
                      <Trash2 size={13} /> Remove
                    </button>
                  </>
                )}
              </div>
              <button type="submit" disabled={saving} className="btn-primary disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </form>
          {feedback && (
            <div className={`mt-2 text-sm rounded-lg px-3 py-2 inline-flex items-start gap-1.5 ${feedback.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {feedback.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <XCircle size={14} className="mt-0.5 shrink-0" />} <span>{feedback.message}</span>
            </div>
          )}
          {integration?.last_sync_at && (
            <p className="mt-2 text-xs text-gray-400">
              Last sync: {new Date(integration.last_sync_at).toLocaleString('en-GB')} —{' '}
              {integration.last_sync_status === 'ok'
                ? <span className="text-emerald-600">ok{integration.last_summary ? ` · ${integration.last_summary.hosts} host(s), ${integration.last_summary.vms} VM(s), ${integration.last_summary.findings} finding(s), ${integration.last_summary.alerts} alert(s)` : ''}
                    {integration.last_summary?.eventsError && <span className="text-amber-600"> · events not read: {integration.last_summary.eventsError}</span>}</span>
                : <span className="text-red-600">error{integration.last_error ? `: ${integration.last_error}` : ''}</span>}
              {/* fullName di vSphere contiene già "build-NNN" (es. "VMware vCenter Server 7.0.3 build-21958406") */}
              {integration.product_name && <> · {integration.product_name}</>}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Connessioni VMware vSphere del tenant (backend/src/jobs/vmwareSync.ts, services/vmware/*): host
// ESXi, VM e il vCenter stesso entrano nell'inventario asset; configurazioni deboli, versioni
// fuori supporto e bollettini VMSA in Vulnerabilities; gli eventi di sicurezza (login falliti, SSH
// abilitato, lockdown disattivato, VM cancellate, snapshot rimossi...) aprono case/alert.
export default function VmwareIntegrationPanel({ tenantId }: { tenantId: number | null }) {
  const [integrations, setIntegrations] = useState<VmwareIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  function load() {
    if (!tenantId) return;
    setLoading(true);
    axios.get('/api/vmware-integration', { params: { tenantId } })
      .then(res => { setIntegrations(res.data.data); setAdding(false); })
      .finally(() => setLoading(false));
  }

  useEffect(load, [tenantId]);

  if (!tenantId) return null;

  return (
    <div className="card p-4 mb-4">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-medium text-sm text-gray-900 inline-flex items-center gap-2">
          <Layers size={15} /> VMware vSphere
        </h2>
        <button type="button" onClick={() => setAdding(true)} className="btn-secondary flex items-center gap-1 text-xs" disabled={adding}>
          <Plus size={13} /> Add connection
        </button>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        vCenter servers and standalone ESXi hosts. Hosts and VMs are merged into the Asset Inventory; weak
        configurations, end-of-life versions and VMSA advisories appear in Vulnerabilities; security events
        (failed logins, SSH/lockdown changes, VM deletions, snapshot removals…) open cases like any other detection.
      </p>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-2">
          {integrations.map(i => <ConnectionRow key={i.id} tenantId={tenantId} integration={i} onChanged={load} />)}
          {adding && <ConnectionRow key="new" tenantId={tenantId} integration={null} onChanged={load} startOpen />}
          {integrations.length === 0 && !adding && <p className="text-xs text-gray-400">No connection configured.</p>}
        </div>
      )}
    </div>
  );
}
